import React, { useEffect, useMemo, useState } from "react";
import { loadGradeSections, saveGradeSections, loadSectionAdvisers, saveSectionAdvisers } from "../../services/sectionsService";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";
import { getCurrentUser } from "../../auth";
import { auth } from "../../services/firebase";
import { getIdTokenResult } from "firebase/auth";

const GRADE_LABELS = [
  "Pre-Kinder","Kinder",
  "Grade 1","Grade 2","Grade 3","Grade 4","Grade 5","Grade 6",
  "Grade 7","Grade 8","Grade 9","Grade 10",
  "Grade 11","Grade 12"
];

export default function SectionsManager() {
  const [campus, setCampus] = useState("gapan");
  const [map, setMap] = useState({});
  const [advisers, setAdvisers] = useState({});
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState("");
  const [newGrade, setNewGrade] = useState("");
  const currentUser = getCurrentUser();

  // #region debug-point B:sections-manager-context
  const dbg = (hypothesisId, msg, data = {}) => fetch("http://127.0.0.1:7777/event", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sessionId: "sections-save-permissions", runId: "pre-fix", hypothesisId, location: "src/pages/Admin/SectionsManager.js", msg: `[DEBUG] ${msg}`, data, ts: Date.now() }) }).catch(() => {});
  // #endregion

  useEffect(() => {
    let active = true;
    setBusy(true);
    Promise.all([loadGradeSections(campus), loadSectionAdvisers(campus)])
      .then(([m, adv]) => {
        if (active) {
          setMap(m || {});
          setAdvisers(adv || {});
        }
      })
      .finally(() => setBusy(false));
    return () => { active = false; };
  }, [campus]);

  const rows = useMemo(() => {
    const existing = new Set(GRADE_LABELS);
    const extras = Array.from(new Set([
      ...Object.keys(map || {}),
      ...Object.keys(advisers || {})
    ])).filter(g => g && !existing.has(g)).sort((a, b) => a.localeCompare(b));
    return [...GRADE_LABELS, ...extras].map(g => ({
      grade: g,
      custom: !existing.has(g),
      sections: Array.isArray(map[g]) ? map[g] : []
    }));
  }, [map, advisers]);

  const addGradeLevel = () => {
    const grade = String(newGrade || "").trim();
    if (!grade) {
      setLog("Please enter a grade level name.");
      return;
    }
    const exists = rows.some(r => String(r.grade).toLowerCase() === grade.toLowerCase());
    if (exists) {
      setLog("Grade level already exists.");
      return;
    }
    setMap(prev => ({ ...(prev || {}), [grade]: [] }));
    setAdvisers(prev => ({ ...(prev || {}), [grade]: {} }));
    setNewGrade("");
    setLog(`Added grade level: ${grade}`);
  };

  const removeGradeLevel = (grade) => {
    setMap(prev => {
      const next = { ...(prev || {}) };
      delete next[grade];
      return next;
    });
    setAdvisers(prev => {
      const next = { ...(prev || {}) };
      delete next[grade];
      return next;
    });
    setLog(`Removed grade level: ${grade}`);
  };

  return (
    <div className="container py-4">
      <h3 className="mb-3">Grade Sections Manager</h3>
      <div className="alert alert-info">
        Manage grade → sections mapping per campus. Changes are saved to {USE_LOCAL_FALLBACK ? "local storage" : "Firestore"}.
      </div>
      <div className="row g-2 mb-3">
        <div className="col-md-3">
          <label className="form-label">Campus</label>
          <select className="form-select" value={campus} onChange={e => setCampus(e.target.value)}>
            <option value="gapan">gapan</option>
            <option value="bulacan">bulacan</option>
          </select>
        </div>
        <div className="col-md-5">
          <label className="form-label">Add Grade Level</label>
          <input
            className="form-control"
            value={newGrade}
            onChange={e => setNewGrade(e.target.value)}
            placeholder="e.g., College 1, SPED, ALS"
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                addGradeLevel();
              }
            }}
          />
        </div>
        <div className="col-md-2 d-flex align-items-end">
          <button className="btn btn-outline-primary w-100" type="button" onClick={addGradeLevel}>
            Add Grade
          </button>
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead>
            <tr>
              <th style={{width: 180}}>Grade Level</th>
              <th>Sections (comma-separated)</th>
              <th>Adviser per Section</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.grade}>
                <td>
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <span>{r.grade}</span>
                    {r.custom ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeGradeLevel(r.grade)}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                </td>
                <td>
                  <input
                    className="form-control"
                    value={r.sections.join(", ")}
                    onChange={e => {
                      const list = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
                      setMap(prev => ({ ...prev, [r.grade]: list }));
                      // Initialize advisers keys for new/removed sections
                      setAdvisers(prev => {
                        const next = { ...(prev || {}) };
                        const g = r.grade;
                        next[g] = next[g] || {};
                        // prune stale advisers
                        Object.keys(next[g]).forEach(sec => { if (!list.includes(sec)) delete next[g][sec]; });
                        list.forEach(sec => { if (!next[g][sec]) next[g][sec] = ""; });
                        return next;
                      });
                    }}
                    placeholder="e.g., Gethsemane, Zion"
                  />
                </td>
                <td>
                  {(r.sections || []).length === 0 ? (
                    <span className="text-muted">No sections</span>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {r.sections.map(sec => (
                        <div className="d-flex align-items-center gap-2" key={`${r.grade}-${sec}`}>
                          <span style={{ minWidth: 140 }}>{sec}</span>
                          <input
                            className="form-control"
                            placeholder="Adviser name"
                            value={(advisers?.[r.grade]?.[sec]) || ""}
                            onChange={e => {
                              const val = e.target.value;
                              setAdvisers(prev => ({
                                ...(prev || {}),
                                [r.grade]: { ...(prev?.[r.grade] || {}), [sec]: val }
                              }));
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="d-flex gap-2">
        <button
          className="btn btn-primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              // #region debug-point B:before-save
              const token = auth.currentUser ? await getIdTokenResult(auth.currentUser, false).catch(() => null) : null;
              dbg("B", "sections save clicked", {
                selectedCampus: campus,
                uiUser: currentUser ? { role: currentUser.role || "", campus: currentUser.campus || "", email: currentUser.email || "", uid: currentUser.uid || "" } : null,
                firebaseUser: auth.currentUser ? { uid: auth.currentUser.uid || "", email: auth.currentUser.email || "" } : null,
                tokenClaims: token?.claims || null,
                gradeCount: Object.keys(map || {}).length,
                adviserGradeCount: Object.keys(advisers || {}).length
              });
              // #endregion
              await saveGradeSections(campus, map);
              // #region debug-point D:grade-sections-save-success
              dbg("D", "gradeSections save succeeded", { selectedCampus: campus });
              // #endregion
              await saveSectionAdvisers(campus, advisers);
              // #region debug-point D:advisers-save-success
              dbg("D", "gradeSectionAdvisers save succeeded", { selectedCampus: campus });
              // #endregion
              setLog("Saved successfully.");
            } catch (e) {
              // #region debug-point D:save-failed
              dbg("D", "sections save failed", {
                selectedCampus: campus,
                errorMessage: e?.message || String(e),
                errorCode: e?.code || "",
                errorName: e?.name || ""
              });
              // #endregion
              setLog(`Failed to save: ${e.message || String(e)}`);
            } finally {
              setBusy(false);
            }
          }}
        >
          Save
        </button>
        <button
          className="btn btn-outline-secondary"
          onClick={async () => {
            setBusy(true);
            const [fresh, adv] = await Promise.all([loadGradeSections(campus), loadSectionAdvisers(campus)]);
            setMap(fresh || {});
            setAdvisers(adv || {});
            setBusy(false);
          }}
        >
          Reload
        </button>
      </div>
      {log && <div className="mt-3 alert alert-secondary">{log}</div>}
    </div>
  );
}
